import React, { PureComponent } from 'react'
import styled from 'styled-components'
import { IMountainPlayerData } from '../../helpers/data/mountainPlayer'

import getPalette, { IPalette } from '../../helpers/colors/colors'

import getFont from '../../helpers/fonts/fonts'

import { ReactComponent as CorrectAnswer } from '../../assets/icons/correct-answer.svg'
import { ReactComponent as NoAnswer } from '../../assets/icons/no-answer.svg'
import { ReactComponent as WrongAnswer } from '../../assets/icons/wrong-answer.svg'
import { IPlayerWithCurrentNumberOfCorrectAnswer } from '../../helpers/data/player'

const colorPalette: IPalette = getPalette()

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	color: white;
	overflow: auto;
	position: relative;
`

const TitleContainer = styled.li`
	width: 100%;
	height: 64px;
	font-family: ${getFont('primaryHeavy')};
	list-style: none;
	padding: 0 4px;
`
const Title = styled.div`
	width: 100%;
	height: 100%;
	display: inline-flex;
	align-items: center;
`

const OverallLeadersTitle = styled.span`
	display: inline-block;
	font-size: 16px;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
	white-space: nowrap;
	font-family: ${getFont('primaryHeavy')};
	margin-left: 15px;
	flex: 1;
`

const AnswersTitle = styled.span`
	display: inline-flex;
	white-space: nowrap;
	justify-content: center;
`

const PointsTitle = styled.span`
	width: 100px;
	display: inline-flex;
	white-space: nowrap;
	justify-content: flex-end;
	padding-right: 14px;
`

const Board = styled.ul`
	width: 100%;
	font-family: ${getFont('primaryHeavy')};
`

const PlayerWrapper = styled.li`
	width: 100%;
	height: 64px;
	font-family: ${getFont('primaryHeavy')};
	list-style: none;
	padding: 0 4px;
`

const Player = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;

	&.isFirst {
		background-color: white;
	}
`

const PlayerPosition = styled.span`
	width: 37px;
	display: inline-block;
	font-family: ${getFont('secondaryRegular')};
	font-size: 32px;
	line-height: 64px;
	text-align: center;
	vertical-align: middle;
`

const PlayerName = styled.span`
	flex: 1;
	overflow: hidden;
`

const PlayerNameText = styled.span`
	display: inline-block;
	font-size: 16px;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
	white-space: nowrap;
	max-width: 97%;
`

const PlayerAnswers = styled.span`
	display: inline-flex;
	white-space: nowrap;
	justify-content: flex-end;
`

const PlayerPoints = styled.span`
	width: 100px;
	display: inline-flex;
	white-space: nowrap;
	justify-content: flex-end;
	padding-right: 14px;
`

const AnswerIconContainer = styled.span`
	display: inline-flex;
	justify-content: center;
`
interface ILeaderboardProps {
	players: IMountainPlayerData[]
	skin?: ILeaderboardSkin
	croppingWidth: string
	totalActivities: number
	currentActivity: number
	shouldShowCorrectAnswers: boolean
	getCurrentNumberOfCorrectAnswersByParticipants?: () => void
	studentsIDWithNumberOfCorrectAnswersFromServer?: any[] | undefined
}

export interface ILeaderboardSkin {
	backgroundColor: string
	player: IPlayerSkin
}

interface IPlayerSkin {
	color: string
}

class Leaderboard extends PureComponent<ILeaderboardProps> {
	state = {
		active: true,
		animationLoaded: false,
		imageLoaded: false,
		studentsIDWithNumberOfCorrectAnswersArray: this.initializeStudentsIDWithNumberOfCorrectAnswersArray(),
	}

	initializeStudentsIDWithNumberOfCorrectAnswersArray() {
		//will not be executed in the Leaders Final page as expected.
		if (this.props.getCurrentNumberOfCorrectAnswersByParticipants) {
			this.props.getCurrentNumberOfCorrectAnswersByParticipants() //trigger the server request to get the studentsIDWithNumberOfCorrectAnswer if exist

			//initialise the array used to count in the frontend the NumberOFCorrectAnswersByStudent,
			// if the website is refreshed this array will be replaced for the content coming from the server in the componentDidUpdate function.
			const studentsIDWithNumberOfCorrectAnswersArray: IPlayerWithCurrentNumberOfCorrectAnswer[] =
				this.props.players.map((player) => {
					return {
						participantID: player.participantID,
						currentNumberOfCorrectAnswer: 0,
						lastActivityUid: '',
					}
				})

			return studentsIDWithNumberOfCorrectAnswersArray
		}

		return []
	}

	componentDidUpdate(prevProps: any) {
		//If the studentsIDWithNumberOfCorrectAnswersFromServer arrive, then update the local state
		if (
			this.props.studentsIDWithNumberOfCorrectAnswersFromServer &&
			prevProps.studentsIDWithNumberOfCorrectAnswersFromServer.length !==
				this.props.studentsIDWithNumberOfCorrectAnswersFromServer.length
		) {
			const studentsIDWithNumberOfCorrectAnswersArray: IPlayerWithCurrentNumberOfCorrectAnswer[] =
				this.props.studentsIDWithNumberOfCorrectAnswersFromServer.map((studentFromServer) => {
					return {
						participantID: studentFromServer.participantID,
						currentNumberOfCorrectAnswer: studentFromServer.currentNumberOfCorrectAnswer,
						lastActivityUid: studentFromServer.lastActivityUid,
					}
				})

			this.setState({
				studentsIDWithNumberOfCorrectAnswersArray,
			})
		}
	}

	buildAnswerIcon = (player: IMountainPlayerData, isFirst = false) => {
		if (player.answered) {
			if (player.isAnswerCorrect) {
				return <CorrectAnswer color={`#8c969b`} style={{ marginBottom: '2px', marginRight: '8px' }} />
			} else
				return (
					<WrongAnswer
						color={`${isFirst ? colorPalette.darkBlue : 'white'}`}
						style={{ width: '13px', marginRight: '8px', position: 'relative', top: '-1px' }}
					/>
				)
		} else
			return (
				<NoAnswer
					color={`${isFirst ? colorPalette.darkBlue : 'white'}`}
					style={{ marginRight: '8px', position: 'relative', top: '5px' }}
				/>
			)
	}

	//get the currentNumberOfCorrectAnswers stored in the state, if the new answer is correct and is not registered it adds +1 and register it in the state, else just return the currentNumberOfCorrectAnswers
	getPlayerCurrentNumberOfCorrectAnswers = (currentPlayer: IMountainPlayerData) => {
		let playerIndexInStoredList = -1

		let currentStudentFromListStored: IPlayerWithCurrentNumberOfCorrectAnswer | undefined =
			this.state.studentsIDWithNumberOfCorrectAnswersArray.find(
				(playerFromTheList: IPlayerWithCurrentNumberOfCorrectAnswer, playerIndex) => {
					//player/student founded in the stored list
					if (playerFromTheList.participantID === currentPlayer.participantID) {
						playerIndexInStoredList = playerIndex
						return true
					} else return false
				}
			)

		//If the current player is not in the list, create it to insert it later if the answer was correct
		if (!currentStudentFromListStored) {
			currentStudentFromListStored = {
				participantID: currentPlayer.participantID,
				currentNumberOfCorrectAnswer: 0,
				lastActivityUid: '',
			}

			playerIndexInStoredList = this.state.studentsIDWithNumberOfCorrectAnswersArray.length
		}

		const currentNumberOfCorrectAnswers = currentStudentFromListStored
			? currentStudentFromListStored.currentNumberOfCorrectAnswer
			: 0

		//add +1 if the current answer is correct and is not registered yet
		if (
			currentPlayer.answered &&
			currentPlayer.isAnswerCorrect &&
			(currentStudentFromListStored && currentStudentFromListStored.lastActivityUid) !== currentPlayer.lastActivityUid
		) {
			const studentsIDWithNumberOfCorrectAnswersArrayClone: IPlayerWithCurrentNumberOfCorrectAnswer[] = [
				...this.state.studentsIDWithNumberOfCorrectAnswersArray,
			]

			//Update the stored value of that student
			studentsIDWithNumberOfCorrectAnswersArrayClone[playerIndexInStoredList] = {
				participantID: currentPlayer.participantID,
				currentNumberOfCorrectAnswer:
					(currentStudentFromListStored && currentStudentFromListStored.currentNumberOfCorrectAnswer + 1) || 1, //increase 1
				lastActivityUid: currentPlayer.lastActivityUid, //register the answer id
			}

			this.setState({
				studentsIDWithNumberOfCorrectAnswersArray: [...studentsIDWithNumberOfCorrectAnswersArrayClone],
			})

			return currentNumberOfCorrectAnswers + 1
		} else {
			return currentNumberOfCorrectAnswers
		}
	}

	buildPlayers = (players: IMountainPlayerData[]) => {
		const color = this.props.skin ? this.props.skin.player.color : colorPalette.primary

		return players
			.sort((a: IMountainPlayerData, b: IMountainPlayerData) => {
				return b.score - a.score
			})
			.map((player, index) => {
				const isFirst = index === 0
				return (
					<PlayerWrapper key={player.playerUID}>
						<Player
							className={isFirst ? 'isFirst' : ''}
							style={{
								color: isFirst ? color : 'white',
							}}
						>
							<PlayerPosition>{index + 1}</PlayerPosition>
							<PlayerName>
								{' '}
								<PlayerNameText> {player.nickname} </PlayerNameText>{' '}
							</PlayerName>

							{this.props.shouldShowCorrectAnswers && (
								<AnswerIconContainer>
									{this.buildAnswerIcon(player, isFirst)}
									<PlayerAnswers>{`${this.getPlayerCurrentNumberOfCorrectAnswers(player) || 0} / ${
										this.props.totalActivities
									}`}</PlayerAnswers>
								</AnswerIconContainer>
							)}
							<PlayerPoints>{player.score}</PlayerPoints>
						</Player>
					</PlayerWrapper>
				)
			})
	}

	render() {
		const backgroundColor = this.props.skin ? this.props.skin.backgroundColor : colorPalette.primary
		return (
			<Wrapper
				style={{
					backgroundColor,
				}}
			>
				<TitleContainer>
					<Title>
						<OverallLeadersTitle style={{ width: this.props.croppingWidth }}>Overall Leaders</OverallLeadersTitle>
						{this.props.shouldShowCorrectAnswers && <AnswersTitle># Correct</AnswersTitle>}
						<PointsTitle>Points</PointsTitle>
					</Title>
				</TitleContainer>
				<Board>{this.buildPlayers(this.props.players)}</Board>
			</Wrapper>
		)
	}
}

export default Leaderboard
